import type { KarmaCompatibility } from "@/lib/karma/types";
import {
  clamp01,
  countElements,
  computeSynergy,
  sharedDigitCount,
} from "@/lib/karma/numerology";

function distanceScore(a: number, b: number) {
  const dist = Math.abs(a - b);
  return clamp01(1 - dist / 8);
}

function normalizeElementCounts(counts: Record<string, number>) {
  const total = Object.values(counts).reduce((acc, v) => acc + v, 0) || 1;
  const normalized: Record<string, number> = {};
  for (const [k, v] of Object.entries(counts)) normalized[k] = v / total;
  return normalized;
}

function elementBalanceScore(pinA: number[], pinB: number[]) {
  const a = normalizeElementCounts(countElements(pinA));
  const b = normalizeElementCounts(countElements(pinB));
  let diff = 0;
  for (const k of Object.keys(a)) {
    diff += Math.abs((a[k] ?? 0) - (b[k] ?? 0));
  }
  return clamp01(1 - diff / 2);
}

export function computeCompatibility(pinA: number[], pinB: number[]): KarmaCompatibility {
  const synergy = computeSynergy(pinA, pinB);
  const sharedDigits = sharedDigitCount(pinA, pinB);
  const elementBalance = elementBalanceScore(pinA, pinB);

  const coreA = pinA.length ? pinA[pinA.length - 1] : 0;
  const coreB = pinB.length ? pinB[pinB.length - 1] : 0;

  const coreScore = distanceScore(coreA, coreB);
  const synergyScore = distanceScore(synergy, 5);
  const sharedScore = clamp01(sharedDigits / 9);

  const score01 =
    0.35 * coreScore + 0.25 * synergyScore + 0.25 * elementBalance + 0.15 * sharedScore;

  const score = Math.round(score01 * 100);

  const notes: string[] = [];
  if (score >= 85) notes.push("Yüksek uyum: akış kolay, iletişim hızlı oturur.");
  else if (score >= 70) notes.push("Güçlü uyum: doğru niyetle çok verimli olur.");
  else if (score >= 55) notes.push("Orta uyum: sabır ve net sınırlar ilişkiyi büyütür.");
  else notes.push("Öğretici uyum: tetiklenen alanlar dönüşüm fırsatı yaratır.");

  if (elementBalance >= 0.8) notes.push("Element dengesi yakın: günlük yaşam ritmi benzer.");
  else if (elementBalance <= 0.45)
    notes.push("Element dengesi farklı: birbirini tamamlamak için alan tanıyın.");

  if (sharedDigits >= 6) notes.push("Kalıp benzerliği yüksek: ortak dil kolay kurulabilir.");
  else if (sharedDigits <= 3) notes.push("Kalıp farklılığı yüksek: merak ve açık iletişim şart.");

  return {
    score,
    synergy,
    sharedDigits,
    elementBalance: Math.round(elementBalance * 100),
    notes,
  };
}
